import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  NotificationService,
  SelectOption,
  SkeletonLoaderComponent,
  StenaButtonComponent,
  StenaSelectComponent,
} from '@app-monorepo/shared/ui/stena-components';
import { CurrencyApiService } from '../../../services/currency-api.service';
import { PeriodsApiService } from '../../../services/periods-api.service';
import { Currency, CurrencyRate } from '../../../models/currency.model';
import { Month, PeriodMode } from '../../../models/period.model';

type RateFormGroup = FormGroup<{
  currencyId: FormControl<number>;
  currencyName: FormControl<string>;
  rateSEK: FormControl<string>;
  rateEUR: FormControl<string>;
}>;

@Component({
  standalone: true,
  selector: 'app-admin-currency-page',
  templateUrl: './admin-currency-page.component.html',
  styleUrl: './admin-currency-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    SkeletonLoaderComponent,
    StenaButtonComponent,
    StenaSelectComponent,
  ],
})
export class AdminCurrencyPageComponent implements OnInit {
  private readonly currencyApi = inject(CurrencyApiService);
  private readonly periodsApi = inject(PeriodsApiService);
  private readonly notify = inject(NotificationService);
  private readonly fb = inject(FormBuilder);

  readonly months = signal<Month[]>([]);
  readonly currencies = signal<Currency[]>([]);
  readonly mode = signal<PeriodMode>('actual');
  readonly selectedMonthId = signal<number | null>(null);
  readonly loading = signal(false);
  readonly loadingRates = signal(false);
  readonly saving = signal(false);

  readonly headerForm = this.fb.group({
    mode: this.fb.control<PeriodMode>('actual', { nonNullable: true }),
    monthId: this.fb.control<number | null>(null),
  });

  readonly rowsArray: FormArray<RateFormGroup> = this.fb.array<RateFormGroup>(
    []
  );

  readonly modeOptions: SelectOption<PeriodMode>[] = [
    { displayName: 'Actual', value: 'actual' },
    { displayName: 'Budget', value: 'budget' },
  ];

  // In budget mode the dropdown shows fiscal-year marker rows (YYYY00) so a
  // single save fans out to all 12 months. In actual mode we show real months.
  readonly monthOptions = computed<SelectOption<number>[]>(() => {
    const all = this.months();
    const filtered =
      this.mode() === 'budget'
        ? all.filter((m) => m.isFiscalYearMarker)
        : all.filter((m) => !m.isFiscalYearMarker);
    return [...filtered]
      .sort((a, b) => b.monthId - a.monthId)
      .map((m) => ({
        displayName: m.isFiscalYearMarker
          ? m.monthName
          : this.formatMonthId(m.monthId),
        value: m.monthId,
      }));
  });

  private formatMonthId(monthId: number): string {
    const year = Math.trunc(monthId / 100);
    const month = monthId % 100;
    return `${year}-${String(month).padStart(2, '0')}`;
  }

  readonly selectLabel = computed(() =>
    this.mode() === 'budget' ? 'Select fiscal year' : 'Select month'
  );

  constructor() {
    this.headerForm.controls.mode.valueChanges.subscribe((mode) => {
      this.mode.set(mode);
      // Mode flip changes the available options; clear stale selection + rows.
      this.headerForm.controls.monthId.setValue(null);
      this.selectedMonthId.set(null);
      this.rowsArray.clear();
    });
    this.headerForm.controls.monthId.valueChanges.subscribe((monthId) => {
      this.selectedMonthId.set(monthId);
      this.rowsArray.clear();
      if (monthId === null) return;
      this.loadRates(monthId, this.mode());
    });
  }

  ngOnInit(): void {
    this.loadMonthsAndCurrencies();
  }

  onSaveRow(row: RateFormGroup): void {
    const monthId = this.selectedMonthId();
    if (monthId === null) return;
    const { currencyId, rateSEK, rateEUR } = row.getRawValue();
    const sek = Number(rateSEK);
    const eur = Number(rateEUR);
    if (!rateSEK || !rateEUR || isNaN(sek) || isNaN(eur)) {
      this.notify.warning('Enter valid SEK and EUR rates');
      return;
    }
    this.saving.set(true);
    this.currencyApi
      .save({
        currencyId,
        monthId,
        mode: this.mode(),
        rateSEK: sek,
        rateEUR: eur,
      })
      .subscribe({
        next: (result) => {
          const count = result.savedMonthIds.length;
          this.notify.success(
            count > 1 ? `Saved for ${count} months` : 'Saved'
          );
          this.saving.set(false);
        },
        error: (err) => {
          this.notify.error(err?.error?.message ?? 'Failed to save rate');
          this.saving.set(false);
        },
      });
  }

  private loadMonthsAndCurrencies(): void {
    this.loading.set(true);
    this.periodsApi.list().subscribe({
      next: (months) => {
        this.months.set(months);
        this.currencyApi.list().subscribe({
          next: (currencies) => {
            this.currencies.set(currencies);
            this.loading.set(false);
          },
          error: () => this.finishLoadWithError('Failed to load currencies'),
        });
      },
      error: () => this.finishLoadWithError('Failed to load months'),
    });
  }

  private loadRates(monthId: number, mode: PeriodMode): void {
    this.loadingRates.set(true);
    this.currencyApi.rates(monthId, mode).subscribe({
      next: (rates) => {
        this.rebuildRows(rates.map((r) => this.toRow(r)));
        this.loadingRates.set(false);
      },
      error: () => {
        this.notify.error('Failed to load rates');
        this.rebuildRows(this.currencies().map((c) => this.emptyRow(c)));
        this.loadingRates.set(false);
      },
    });
  }

  private rebuildRows(rows: RateRow[]): void {
    this.rowsArray.clear();
    for (const row of rows) {
      this.rowsArray.push(
        this.fb.group({
          currencyId: this.fb.control(row.currencyId, { nonNullable: true }),
          currencyName: this.fb.control(row.currencyName, {
            nonNullable: true,
          }),
          rateSEK: this.fb.control(row.rateSEK, { nonNullable: true }),
          rateEUR: this.fb.control(row.rateEUR, { nonNullable: true }),
        })
      );
    }
  }

  private toRow(r: CurrencyRate): RateRow {
    return {
      currencyId: r.currencyId,
      currencyName: r.currencyName,
      rateSEK: r.rateSEK ?? '',
      rateEUR: r.rateEUR ?? '',
    };
  }

  private emptyRow(c: Currency): RateRow {
    return {
      currencyId: c.currencyId,
      currencyName: c.currencyName,
      rateSEK: '',
      rateEUR: '',
    };
  }

  private finishLoadWithError(message: string): void {
    this.notify.error(message);
    this.loading.set(false);
  }
}

interface RateRow {
  currencyId: number;
  currencyName: string;
  rateSEK: string;
  rateEUR: string;
}
